import * as React from "react";
import Text, { TextInput } from "components/form/Text/Text";
import Popover from "components/Popover/Popover";
import { DataBaseComponent, DataBaseComponentState } from "components/DataBaseComponent/DataBaseComponent";
import BusyIndicator from "components/BusyIndicator/BusyIndicator";
import BusyFromPromise from "components/BusyFromPromise/BusyFromPromise";
import DebounceValue from "components/DebounceValue/DebounceValue";
const styles = require("./style.less");
import IconButton, { Icon } from "components/IconButton/IconButton";
import ErrorPanel from "components/ErrorPanel/ErrorPanel";
import UseLabelStrategy from "components/LabelStrategy/LabelStrategy";
import IconButtonList from "../IconButtonList/IconButtonList";
import VirtualListWithKeyboard from "components/VirtualListWithKeyboard/VirtualListWithKeyboard";
import { FocusableComponent } from "components/VirtualListWithKeyboard/FocusableComponent";
import { withBoundField } from "components/form/BoundField/BoundField";
const keycode = require("keycode");

export interface AutoCompleteProps {
    allowAnyTextValue?: boolean; // set to true if you are allowed to enter a value that does not appear in the popup
    label?: string;
    value: string;
    error?: string;
    hintText: string;
    showEmpty?: boolean;
    getOptions(searchText: string): Promise<AutoCompleteSearchResults>;
    onChange(value: string): void;
    textInputRef?(textInput: TextInput | null): void;
}

export interface AutoCompleteSearchResults {
    items: ReadonlyArray<AutoCompleteOption>;
    containsAllResults: boolean;
}

export interface AutoCompleteOption {
    Id: string;
    Name: string;
    display?: React.ReactNode;
}

interface AutoCompleteState extends DataBaseComponentState {
    open: boolean;
    displayedValue: string;
    options: AutoCompleteSearchResults | null;
    hintText?: string;
}

const DebounceText = DebounceValue(Text);
const VirtualList = VirtualListWithKeyboard<AutoCompleteOption>();

class AutoCompleteInternal extends DataBaseComponent<AutoCompleteProps, AutoCompleteState> {
    static defaultProps: Partial<AutoCompleteProps> = {
        showEmpty: true,
    };

    private textField: TextInput | null = null;
    private textAnchor: HTMLDivElement | undefined;
    private popoverContent: HTMLDivElement | undefined;
    private virtualList: FocusableComponent | undefined;
    private timeoutId: NodeJS.Timer | undefined;
    private updatePopoverPosition: () => void;

    constructor(props: AutoCompleteProps) {
        super(props);
        this.state = {
            open: false,
            displayedValue: props.value,
            options: null,
            hintText: null,
        };
    }

    componentWillMount() {
        if (this.props.textInputRef) {
            this.props.textInputRef(this);
        }
    }

    componentDidMount() {
        window.document.addEventListener("keydown", this.onGlobalKeyDown);
    }

    componentWillUnmount() {
        this.cancelTimerToTrackNavigateAway();
        window.document.removeEventListener("keydown", this.onGlobalKeyDown);
        if (this.props.textInputRef) {
            this.props.textInputRef(null);
        }
    }

    componentWillReceiveProps(nextProps: AutoCompleteProps) {
        if (nextProps.value !== this.props.value) {
            this.setState({ displayedValue: nextProps.value });
        }
    }

    render() {
        return (
            <div className={styles.autoCompleteContainer}>
                <div ref={textAnchor => (this.textAnchor = textAnchor)} className={styles.textContainer} onKeyDown={ev => this.onKeyDown(ev)}>
                    <DebounceText
                        textInputRef={textField => (this.textField = textField)}
                        label={this.props.label}
                        value={this.state.displayedValue}
                        error={this.props.error}
                        hintText={this.state.hintText}
                        onChange={value => this.onSearchTextChange(value)}
                        onFocus={this.onFocus}
                        onClick={ev => {
                            ev.stopPropagation(); // This could bubble up and close the popup, even though the same mousedown event opened it (by focusing the input)
                            this.onFocus();
                        }}
                        onBlur={ev => this.onBlurFromText(ev)}
                    />
                    <div className={styles.buttons}>
                        <IconButtonList buttons={this.buttons()} />
                    </div>
                </div>

                <Popover
                    open={this.state.open}
                    anchorEl={this.textAnchor}
                    onClose={() => this.setState({ open: false })}
                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                    transformOrigin={{ horizontal: "left", vertical: "top" }}
                    disableAutoFocus={true}
                    className={styles.popover}
                    getUpdatePosition={update => (this.updatePopoverPosition = update)}
                >
                    <div
                        onFocus={this.cancelTimerToTrackNavigateAway}
                        onBlur={this.startTimerToTrackNavigateAway}
                        // Todo: review this change. It was added to allow annotation suggestions to be displayed
                        // at a reasonable size.
                        style={{ minWidth: this.textAnchor ? `${Math.max(450, this.textAnchor.offsetWidth)}px` : undefined }}
                        ref={popoverContent => (this.popoverContent = popoverContent)}
                    >
                        <div className={styles.popoverContent}>
                            <BusyFromPromise promise={this.state.busy} children={busy => <BusyIndicator show={busy} />} />
                            {this.renderErrors()}
                            {!this.state.options && this.state.busy && <div className={styles.popoverInfoText}>Searching...</div>}
                            {this.state.options && (
                                <VirtualList
                                    maxHeightInRem={17.5}
                                    multiSelectRef={el => (this.virtualList = el)}
                                    items={this.state.options.items}
                                    renderItem={item => ({ primaryText: item.display || item.Name })}
                                    onSelected={id => this.selectOption(id)}
                                    onResized={() => {
                                        // When the content's size changes, we re-render so that the
                                        // popover can re-position itself based on the new `VirtualList` size
                                        if (this.updatePopoverPosition) {
                                            this.updatePopoverPosition();
                                        }
                                    }}
                                    onBlur={() => this.textField.focus()}
                                />
                            )}
                        </div>
                        {/*This bit is outside of the scrollable container so that it always displays*/}
                        {this.state.options && !this.state.options.containsAllResults && <div className={styles.warning}>Only showing {this.state.options.items.length} options, for more results narrow your search criteria</div>}
                    </div>
                </Popover>
            </div>
        );
    }

    renderErrors() {
        const errors = this.state.errors;
        if (!errors) {
            return null;
        }
        return <ErrorPanel message={errors.message} details={errors.details} detailLinks={errors.detailLinks} helpText={errors.helpText} helpLink={errors.helpLink} scrollToPanel={false} />;
    }

    isFocused() {
        return this.textField && this.textField.isFocused();
    }

    focus() {
        if (this.textField) {
            this.textField.focus();
        }
    }

    forceHasValue() {
        if (this.textField) {
            return this.textField.forceHasValue();
        }
    }

    insertAtCursor(value: string) {
        if (!this.textField) {
            return;
        }
        this.textField.insertAtCursor(value);
    }

    setValueAndSelection = (selection: { start: any; end: any }, value: string) => {
        this.textField.setValueAndSelection(selection, value);
    };

    getSelection = () => {
        return this.textField && this.textField.getSelection();
    };

    select() {
        if (this.textField) {
            this.textField.select();
        }
    }
    private cancelTimerToTrackNavigateAway = () => {
        clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
    };

    private startTimerToTrackNavigateAway = () => {
        if (this.timeoutId === undefined) {
            this.timeoutId = setTimeout(() => this.onNavigateAwayFromAutoComplete(), 0);
        }
    };

    private focusText() {
        if (this.textField && !this.textField.isFocused()) {
            this.textField.focus();
        }
    }

    private onBlurFromText(ev: React.FocusEvent<HTMLDivElement>) {
        this.setState({ hintText: null });
        this.startTimerToTrackNavigateAway();
    }

    private onNavigateAwayFromAutoComplete() {
        this.setState({ open: false, displayedValue: this.props.value });
    }

    private onFocus = () => {
        this.cancelTimerToTrackNavigateAway();
        this.setState({ hintText: this.props.hintText });

        if (!this.props.showEmpty && (!this.state || this.state.displayedValue === "")) {
            this.setState({ open: false, options: null });
            return;
        }
    };

    private onKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
        const code = keycode(ev);
        if (code === "down") {
            if (this.state.options && this.state.options.items.length > 0) {
                this.setState({ open: true });
            }

            if (this.virtualList && this.state.options && this.state.options.items.length) {
                ev.preventDefault();
                this.virtualList.focus();
            }
        }

        if (code === "enter" && this.state.options.items.length === 1) {
            this.selectOption(this.state.options.items[0].Id);
        }
    }

    private async onSearchTextChange(searchText: string) {
        const open = searchText !== "" || this.props.showEmpty;
        this.setState(prev => ({ displayedValue: searchText, open, options: prev.open ? prev.options : null }));
        if (this.props.allowAnyTextValue) {
            this.props.onChange(searchText);
        }
        if (open) {
            await this.requestOptions(searchText);
        }
    }

    private async requestOptions(searchText: string) {
        await this.doBusyTask(async () => {
            const options = await this.props.getOptions(searchText);
            this.setState({ options });
        });
    }

    private selectOption(id: string) {
        const option = this.state.options.items.find(i => i.Id === id);
        this.setState({ displayedValue: option.Name, open: false });
        this.props.onChange(option.Id);
    }

    private clearValue() {
        this.setState({ open: false, displayedValue: "" });
        this.props.onChange("");
    }

    private onGlobalKeyDown = (event: KeyboardEvent) => {
        const code = keycode(event);
        if (code === "esc") {
            if (this.state.open) {
                this.setState({ open: false, options: null, displayedValue: this.props.value });
            }
        }
    };

    private buttons() {
        return [!!this.props.value && <IconButton icon={Icon.Cancel} onClick={() => this.clearValue()} tabIndex={-1} />, <IconButton icon={Icon.ArrowDown} onClick={() => this.focusText()} tabIndex={-1} />].filter(b => !!b);
    }
}

const AutoComplete = UseLabelStrategy(AutoCompleteInternal, fieldName => fieldName);
export default AutoComplete;

export const BoundAutoComplete = withBoundField((props: AutoCompleteProps) => (
    <div style={{ display: "flex", alignItems: "baseline", width: "100%" }}>
        <AutoComplete {...props} />
    </div>
));
